import { CloseCircleFilled, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { feedbackApi } from "api/feedback.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  CustomerCell,
  StatusCell,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useLocation } from "react-router-dom";
import { ActivityLogLocationState } from "types/activityLog";
import { fetchCustomerOptions, fetchStaffOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import { QueryParam } from "types/query";
import { Feedback, FeedbackCounts, FeedbackStatus } from "types/feedback";
import { getTitle } from "utils";
import { can } from "utils/auth";
import { formatDateTime } from "utils/date";
import { StatusTabs } from "views/Opportunity/components/StatusTabs";
import { FeedbackModal } from "./components/FeedbackModal";
import {
  FEEDBACK_PATH,
  emptyFeedbackCounts,
  feedbackStatusTrans,
  feedbackTabs,
  staffName,
} from "./feedback.meta";
import "./Feedback.scss";

const countOf = (counts: FeedbackCounts, key: string) => {
  if (key === "ALL") return counts.all;
  return counts.statuses[key as FeedbackStatus] || 0;
};

export const FeedbackPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("ALL");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Feedback[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<FeedbackCounts>(emptyFeedbackCounts());
  const feedbackModalRef = useRef<FeedbackModal>(null);
  const openedFromLogRef = useRef(false);
  const location = useLocation();

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  useEffect(() => {
    if (openedFromLogRef.current) {
      return;
    }
    const feedback = (location.state as ActivityLogLocationState | null)?.feedback;
    if (!feedback) {
      return;
    }
    openedFromLogRef.current = true;
    feedbackModalRef.current?.handleUpdate(feedback);
  }, [location.state]);

  const fetchData = useCallback(async (queryParams: QueryParam, tabKey: string) => {
    setLoading(true);
    try {
      const params: QueryParam = { ...queryParams };
      if (tabKey !== "ALL") {
        params.status = tabKey;
      }
      const res = await feedbackApi.findAll(params);
      setData(res.data.feedbacks || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || emptyFeedbackCounts());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query, tab);
  }, [fetchData, query, tab]);

  const handleDelete = useCallback(
    async (feedback: Feedback) => {
      setLoading(true);
      try {
        await feedbackApi.delete(feedback.id);
        message.success("Xóa phản hồi thành công");
        await fetchData(query, tab);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query, tab],
  );

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const stopRowClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  const openView = useCallback((feedback: Feedback) => {
    feedbackModalRef.current?.handleView(feedback);
  }, []);

  const openEdit = useCallback((feedback: Feedback) => {
    feedbackModalRef.current?.handleUpdate(feedback);
  }, []);

  const openCreate = useCallback(() => {
    feedbackModalRef.current?.handleCreate();
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handleTabChange = useCallback((key: string) => {
    setTab(key);
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const refresh = useCallback(() => {
    fetchData(query, tab);
  }, [fetchData, query, tab]);

  const customerFilter = useRemoteOptions(fetchCustomerOptions);
  const handledStaffFilter = useRemoteOptions(fetchStaffOptions);

  const setQueryFilter = useCallback((key: string, value?: number) => {
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (value) {
        next[key] = value;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const filters = useMemo(
    () => [
      {
        key: "customerId",
        value: (query.customerId as number | undefined) ?? "",
        options: customerFilter.options,
        onChange: (value: string | number | undefined) => {
          const customerId = typeof value === "number" ? value : undefined;
          customerFilter.select(customerId);
          setQueryFilter("customerId", customerId);
        },
        placeholder: "Khách hàng",
        allowClear: true,
        showSearch: true,
        filterOption: false as const,
        onSearch: customerFilter.onSearch,
        width: 220,
      },
      {
        key: "handledStaffId",
        value: (query.handledStaffId as number | undefined) ?? "",
        options: handledStaffFilter.options,
        onChange: (value: string | number | undefined) => {
          const staffId = typeof value === "number" ? value : undefined;
          handledStaffFilter.select(staffId);
          setQueryFilter("handledStaffId", staffId);
        },
        placeholder: "Người xử lý",
        allowClear: true,
        showSearch: true,
        filterOption: false as const,
        onSearch: handledStaffFilter.onSearch,
        width: 200,
      },
    ],
    [customerFilter, handledStaffFilter, query.customerId, query.handledStaffId, setQueryFilter],
  );

  const canCreate = can(FEEDBACK_PATH, "CREATE");
  const canEdit = can(FEEDBACK_PATH, "UPDATE");
  const canDelete = can(FEEDBACK_PATH, "DELETE");

  const tabs = feedbackTabs.map((item) => ({
    ...item,
    count: countOf(counts, item.key),
  }));

  const columns = useMemo<TableColumnsType<Feedback>>(
    () => [
      {
        title: "Thời gian",
        dataIndex: "feedbackAt",
        key: "feedbackAt",
        width: 160,
        render: (value: number) => <TableContent>{formatDateTime(value)}</TableContent>,
      },
      {
        title: "Khách hàng",
        key: "customer",
        width: 220,
        render: (_, record) => <CustomerCell customer={record.customer} />,
      },
      {
        title: "Nội dung",
        dataIndex: "content",
        key: "content",
        width: 240,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Ghi chú nội bộ",
        dataIndex: "note",
        key: "note",
        width: 200,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (value: FeedbackStatus) => <StatusCell value={value} trans={feedbackStatusTrans} />,
      },
      {
        title: "Người tạo",
        key: "createdStaff",
        width: 150,
        render: (_, record) => <TableContent>{staffName(record.createdStaff)}</TableContent>,
      },
      {
        title: "Người xử lý",
        key: "handledStaff",
        width: 150,
        render: (_, record) => <TableContent>{staffName(record.handledStaff)}</TableContent>,
      },
      {
        title: "Thời gian hoàn thành",
        dataIndex: "completedAt",
        key: "completedAt",
        width: 170,
        render: (value: number) => <TableContent>{formatDateTime(value)}</TableContent>,
      },
      ...(canEdit || canDelete
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 120,
            align: "center" as const,
            fixed: "right" as const,
            render: (_: unknown, record: Feedback) => (
              <div className="flex gap-2 justify-center items-center" onClick={stopRowClick}>
                {canEdit && (
                  <Tooltip title="Cập nhật">
                    <ActionButton aria-label="Cập nhật" onClick={() => openEdit(record)}>
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      cancelText="Không"
                      okText="Xóa"
                      onConfirm={() => handleDelete(record)}
                      title="Xác nhận xóa?"
                    >
                      <ActionButton aria-label="Xóa">
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [canDelete, canEdit, handleDelete, openEdit, stopRowClick],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div className="feedbackPage">
      <StatusTabs items={tabs} activeKey={tab} onChange={handleTabChange} />
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 360,
              placeholder: "Tìm theo nội dung",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            leftActions={
              canCreate && (
                <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Tạo mới
                </AppButton>
              )
            }
            filterLabel={null}
            filters={filters}
          />
        }
        table={
          <AppTable<Feedback>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            onRow={(record) => ({
              onClick: () => openView(record),
            })}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />

      <FeedbackModal ref={feedbackModalRef} onSubmitOk={refresh} onClose={() => { }} />
    </div>
  );
});
